
import React, { useState } from 'react';
import { PortfolioItem } from './types';
import { Icon } from './components/Icon';
import { OrderModal } from './components/OrderModal';
import { APP_SETTINGS } from './constants';

interface PortfolioAppProps {
  items: PortfolioItem[];
  onBack: () => void;
  contactNumber: string;
}

const PortfolioApp: React.FC<PortfolioAppProps> = ({ items, onBack, contactNumber }) => {
  const appItems = items.filter(item => item.category === 'aplikasi');
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('');

  const handleOrder = (pkgName: string) => {
    setSelectedPackage(pkgName);
    setIsOrderOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#030712] pt-32 pb-20">
      <OrderModal 
        isOpen={isOrderOpen} 
        onClose={() => setIsOrderOpen(false)} 
        selectedPackage={selectedPackage} 
        contactNumber={contactNumber}
      />

      <div className="container mx-auto px-6">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors group"
        >
          <div className="p-2 glass-morphism rounded-lg group-hover:bg-white/10">
            <Icon name="ArrowRight" className="rotate-180" size={18} />
          </div>
          <span>Kembali ke Beranda</span>
        </button>

        <div className="mb-16">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Portofolio <span className="gradient-text">Aplikasi Web</span></h1>
          <p className="text-slate-400 max-w-2xl">Sistem informasi dan aplikasi berbasis web (Web-Based App) untuk efisiensi operasional bisnis Anda.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-32">
          {appItems.length > 0 ? appItems.map((item) => (
            <div key={item.id} className="glass-morphism rounded-3xl overflow-hidden border border-white/10 hover:border-blue-500/50 transition-all group">
              <div className="aspect-video relative overflow-hidden">
                <img 
                  src={item.imageUrl || `https://picsum.photos/seed/${item.id}/600/400`} 
                  alt={item.title} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                   <a 
                    href={item.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-full font-bold text-sm"
                   >
                     Coba Demo <Icon name="ExternalLink" size={14} />
                   </a>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm mb-4 line-clamp-2">{item.description}</p>
                <div className="flex gap-2 mb-4">
                   <span className="text-[10px] px-2 py-1 bg-blue-500/20 text-blue-400 rounded-md font-bold uppercase tracking-wider">Web App</span>
                   <span className="text-[10px] px-2 py-1 bg-purple-500/20 text-purple-400 rounded-md font-bold uppercase tracking-wider">Dashboard</span>
                </div>
                <a 
                  href={item.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 font-semibold text-sm hover:underline flex items-center gap-1"
                >
                  Detail Project <Icon name="ChevronRight" size={14} />
                </a>
              </div>
            </div>
          )) : (
            <div className="col-span-full py-20 text-center glass-morphism rounded-3xl border border-dashed border-white/20">
              <p className="text-slate-500">Belum ada portofolio aplikasi yang ditambahkan.</p>
            </div>
          )}
        </div>

        {/* App Pricing Section */}
        <div className="mb-12">
           <div className="text-center mb-16 max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-morphism mb-6 border border-blue-500/30">
                    <Icon name="Globe" size={16} className="text-blue-400" />
                    <span className="text-xs font-bold text-blue-300 tracking-wider uppercase">Web-Based Solution</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-extrabold mb-6 text-white">Paket Pengembangan Sistem</h2>
                <p className="text-slate-400 leading-relaxed text-lg">
                    Aplikasi berbasis web yang dapat diakses dari perangkat manapun melalui browser, tanpa perlu instalasi yang rumit.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* App Package 1 */}
                <div className="glass-morphism rounded-3xl p-8 border border-white/10 flex flex-col hover:border-blue-500/50 transition-all">
                    <div className="mb-6">
                         <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                            <Icon name="Layout" className="text-blue-400" size={24} />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">STARTER APP</h3>
                        <p className="text-slate-400 text-xs uppercase font-bold tracking-widest">Sistem Sederhana</p>
                    </div>
                    <div className="mb-6">
                        <span className="text-3xl font-extrabold text-white">Rp 2.5jt</span>
                    </div>
                     <p className="text-sm text-slate-400 mb-6">
                        Aplikasi Web fungsional untuk kebutuhan spesifik seperti input data, kalkulator custom, atau formulir cerdas.
                    </p>
                    <ul className="space-y-3 mb-8 flex-1">
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-500" /> Web-Based Application</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-500" /> CRUD Data Dasar</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-500" /> Akses via Browser (HP/Laptop)</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-500" /> Database Terintegrasi</li>
                    </ul>
                    <button onClick={() => handleOrder('App - Starter Web')} className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl font-bold text-sm text-center transition-all">Pilih Paket Starter</button>
                </div>

                {/* App Package 2 */}
                <div className="glass-morphism rounded-3xl p-8 border border-purple-500/50 bg-gradient-to-b from-purple-500/5 to-transparent flex flex-col relative transform md:-translate-y-4">
                    <div className="absolute top-0 right-0 bg-purple-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl rounded-tr-2xl">MOST VALUE</div>
                    <div className="mb-6">
                         <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                            <Icon name="Database" className="text-purple-400" size={24} />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">BUSINESS PRO</h3>
                        <p className="text-purple-300 text-xs uppercase font-bold tracking-widest">Management System</p>
                    </div>
                     <div className="mb-6">
                        <span className="text-3xl font-extrabold text-white">Rp 7jt</span>
                    </div>
                     <p className="text-sm text-slate-400 mb-6">
                        Sistem Informasi Manajemen kompleks. Kasir (POS), Inventory, Karyawan, Sekolah, atau Keuangan.
                    </p>
                    <ul className="space-y-3 mb-8 flex-1">
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-purple-500" /> <strong>Multi-User Role & Login</strong></li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-purple-500" /> Dashboard Admin Lengkap</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-purple-500" /> Export Laporan (PDF/Excel)</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-purple-500" /> Fitur Pencarian & Filter Canggih</li>
                    </ul>
                    <button onClick={() => handleOrder('App - Business Pro')} className="w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-xl font-bold text-sm text-center text-white shadow-lg shadow-purple-500/20 transition-all">Konsultasi Business</button>
                </div>

                 {/* App Package 3 */}
                 <div className="glass-morphism rounded-3xl p-8 border border-blue-400/20 flex flex-col hover:border-blue-400/50 transition-all">
                    <div className="mb-6">
                         <div className="w-12 h-12 bg-blue-400/10 rounded-xl flex items-center justify-center mb-4">
                            <Icon name="Settings" className="text-blue-300" size={24} />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">ENTERPRISE</h3>
                        <p className="text-slate-400 text-xs uppercase font-bold tracking-widest">Custom Ecosystem</p>
                    </div>
                    <div className="mb-6">
                        <span className="text-3xl font-extrabold text-white">Custom</span>
                    </div>
                     <p className="text-sm text-slate-400 mb-6">
                        Sistem skala besar (SAAS), Integrasi API pihak ketiga (Payment Gateway, WA Blast), dan kebutuhan khusus lainnya.
                    </p>
                    <ul className="space-y-3 mb-8 flex-1">
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-300" /> Custom Business Logic</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-300" /> Integrasi API & Payment</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-300" /> High Performance Server</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-300" /> Maintenance & Support Prioritas</li>
                    </ul>
                    <button onClick={() => handleOrder('App - Enterprise Custom')} className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl font-bold text-sm text-center transition-all">Hubungi Sales</button>
                </div>
            </div>
        </div>

      </div>
    </div>
  );
};

export default PortfolioApp;
